import * as THREE from 'three'
import { useRef, useState } from 'react'
import { easing } from 'maath'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, ContactShadows } from '@react-three/drei'
import {
  Center,
  Caustics,
  Environment,
  Lightformer,
  RandomizedLight,
  PerformanceMonitor,
  MeshTransmissionMaterial
} from '@react-three/drei'

// Material used for inner glass effects (if needed)
const innerMaterial = new THREE.MeshStandardMaterial({
  transparent: true,
  opacity: 1,
  color: 'black',
  roughness: 0,
  side: THREE.FrontSide,
  blending: THREE.AdditiveBlending,
  polygonOffset: true,
  polygonOffsetFactor: 1,
  envMapIntensity: 2
})

export default function App() {
  const [perfSucks, degrade] = useState(false)

  return (
    <Canvas
      shadows
      dpr={[1, perfSucks ? 1.5 : 2]}
      eventSource={document.getElementById('root')}
      eventPrefix="client"
      camera={{ position: [4, 2, 6], fov: 35 }}
    >
      {/** Monitor performance and lower quality if necessary */}
      <PerformanceMonitor onDecline={() => degrade(true)} />
      <color attach="background" args={['#e0e0e0']} />
      
      <group position={[2, -0.5, 0]} rotation={[0, 0, 0]} scale={[0.15, 0.15, 0.15]}>
        <Scene />
      </group>

      {/** ✨ Soft, realistic contact shadows */}
      <ContactShadows
        opacity={0.4}        // Shadow darkness
        blur={3}             // Makes the shadow soft
        resolution={1024}    // High-res shadow
        scale={10}           // Size of shadow area
        position={[0, -0.5, 0]}
      />

      <Env perfSucks={perfSucks} />
    </Canvas>
  )
}

function Scene(props) {
  // Load the GLB model
  const { scene } = useGLTF('/untitled8.glb') // <-- Replace with your GLB file path

  return (
    <group {...props}>
      <primitive object={scene} dispose={null} />
    </group>
  )
}

function Env({ perfSucks }) {
  const ref = useRef()

  useFrame((state, delta) => {
    if (!perfSucks) {
      easing.damp3(ref.current.rotation, [Math.PI / 2, 0, state.clock.elapsedTime / 5 + state.pointer.x], 0.2, delta)
      easing.damp3(
        state.camera.position,
        [Math.sin(state.pointer.x / 4) * 9, 1.25 + state.pointer.y, Math.cos(state.pointer.x / 4) * 9],
        0.5,
        delta
      )
      state.camera.lookAt(0, 0, 0)
    }
  })

  return (
    <Environment frames={perfSucks ? 1 : Infinity} preset="city" resolution={256} background blur={0.8}>
      <Lightformer intensity={4} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={[10, 10, 1]} />
      <Lightformer intensity={4} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={[10, 10, 1]} />
      <group rotation={[Math.PI / 2, 1, 0]}>
        {[2, -2, 2, -4, 2, -5, 2, -9].map((x, i) => (
          <Lightformer key={i} intensity={1} rotation={[Math.PI / 4, 0, 0]} position={[x, 4, i * 4]} scale={[4, 1, 1]} />
        ))}
        <Lightformer intensity={0.5} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={[50, 2, 1]} />
        <Lightformer intensity={0.5} rotation-y={Math.PI / 2} position={[-5, -1, -1]} scale={[50, 2, 1]} />
        <Lightformer intensity={0.5} rotation-y={-Math.PI / 2} position={[10, 1, 0]} scale={[50, 2, 1]} />
      </group>
      <group ref={ref}>
        <Lightformer
          intensity={5}
          form="ring"
          color="#e5dfca"
          rotation-y={Math.PI / 2}
          position={[-5, 2, -1]}
          scale={[10, 10, 1]}
        />
      </group>
    </Environment>
  )
}
